import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import * as moment from 'moment/moment';
import { lastValueFrom, Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { MeService } from '../api';

import 'moment/locale/cs';
import { LoadingOverlayComponent } from './common/components/loading-overlay/loading-overlay.component';
import { LayoutComponent } from './core/layout/ui/layout/layout.component';
import { CustomGroupService } from './core/services/custom-group/custom-group.service';
import { CustomOperatorsService } from './core/services/custom-operators/custom-operators.service';
import { CustomVehicleService } from './core/services/custom-vehicle/custom-vehicle.service';
import { GoogleMapsService } from './core/services/google-maps/google-maps.service';
import { ThemeService } from './core/services/theme';
import { LocalStorageItem, setLocalStorageItem } from './core/utils';
import { AuthService } from './states/auth/services/auth.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  imports: [RouterOutlet, AsyncPipe, LayoutComponent, LoadingOverlayComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  private themeService = inject(ThemeService);
  private authService = inject(AuthService);
  private meService = inject(MeService);
  private customGroupService = inject(CustomGroupService);
  private customVehicleService = inject(CustomVehicleService);
  private customOperatorsService = inject(CustomOperatorsService);
  private googleMapsService = inject(GoogleMapsService);
  private router = inject(Router);
  private titleService = inject(Title);
  private _renderer = inject(Renderer2);

  // Set here rather than in ngOnInit: the async pipe subscribes during the
  // first render, and a later assignment would not be picked up.
  readonly isLoggedIn$: Observable<boolean> = this.authService.isLoggedIn$;
  readonly isAuthPending = this.authService.isAuthPending;
  subscriptionSetEventTitle: Subscription | null = null;
  subscriptionRouteChanged: Subscription | null = null;

  public intervalReloadCache: ReturnType<typeof setInterval> | null = null;

  async ngOnInit(): Promise<void> {
    this.runGlobalServices();
    moment.locale('cs'); //setup localization dates

    //Custom title
    this.subscriptionSetEventTitle = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route: ActivatedRoute = this.router.routerState.root;
          let routeTitle = '';
          while (route?.firstChild) {
            route = route.firstChild;
          }
          if (route.snapshot.data['title']) {
            routeTitle = route?.snapshot.data['title'];
          }
          return routeTitle;
        }),
      )
      .subscribe((title: string) => {
        if (title) {
          this.titleService.setTitle(`${title} | ` + $localize`Data z auta`);
        } else {
          this.titleService.setTitle($localize`Data z auta`);
        }
      });

    this.subscriptionRouteChanged = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        //Remove left filter default class
        this._renderer.removeClass(document.body, 'left-filter-visible');
      });

    this.authService.isLoggedIn$
      .pipe(filter((isLoggedIn) => isLoggedIn))
      .subscribe(() => {
        this.initializeAppData();
      });
  }

  private async initializeAppData(): Promise<void> {
    await this.customGroupService.groupListCached(true);
    await this.customVehicleService.vehicleListAllCached(true);
    await this.customOperatorsService.operatorsListAllCached(true);

    if (this.intervalReloadCache) {
      clearInterval(this.intervalReloadCache);
    }
    this.intervalReloadCache = setInterval(async () => {
      // A failed refresh must not escape the interval callback: an unhandled
      // rejection here is reported to Sentry as an unhandled error, and the
      // tick is transient anyway — the next one an hour later retries.
      try {
        await this.reloadMeData();
        await this.customGroupService.groupListCached(true);
        await this.customVehicleService.vehicleListAllCached(true);
        await this.customOperatorsService.operatorsListAllCached(true);
      } catch {
        // Network blip, sleeping machine or an expired session (the
        // interceptor already signs the user out on 401).
      }
    }, 3600000); //1hour interval
  }

  private async reloadMeData() {
    const meResponse = await lastValueFrom(this.meService.me());
    setLocalStorageItem(LocalStorageItem.meInformation, meResponse);
  }

  ngOnDestroy(): void {
    if (this.subscriptionSetEventTitle) {
      this.subscriptionSetEventTitle?.unsubscribe();
    }
    if (this.subscriptionRouteChanged) {
      this.subscriptionRouteChanged?.unsubscribe();
    }
    if (this.intervalReloadCache) {
      clearInterval(this.intervalReloadCache);
    }
  }

  private runGlobalServices(): void {
    this.themeService.init();
  }

  public async signOut(): Promise<void> {
    await this.authService.signOut(undefined, false);
  }
}
